import { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Menu,
  Bell,
  BellRing,
  BellOff,
  Settings,
  LogOut,
  User,
  Shield,
  UserPlus,
  ClipboardList,
  ArrowDownCircle,
  ArrowUpCircle,
  MessageCircle,
  HelpCircle,
  TrendingUp,
  Activity, CandlestickChart, LogIn, Radio , X } from 'lucide-react'
import i18n from '@/i18n'
import { useAuthStore } from '@/store/authStore'
import { useNotificationStore } from '@/store/notificationStore'
import { usePendingCountsStore } from '@/store/pendingCountsStore'
import { useHandoverUnreadStore } from '@/store/handoverUnreadStore'
import { dashboardService, HeaderStats } from '@/services/dashboardService'
import { apiService } from '@/services/api'
import { createPortal } from 'react-dom'
import LanguageSwitcher from './LanguageSwitcher'
import {
  isPushSupported,
  getPermissionState,
  enablePush,
  disablePush,
  getCurrentSubscription,
  sendTestPush,
} from '@/services/pushNotificationService'

interface HeaderProps {
  onMenuClick: () => void
}

const fmtKRW = (v: number) => {
  const abs = Math.abs(v)
  const sign = v < 0 ? '-' : ''
  return `${sign}${Math.round(abs).toLocaleString()}`
}

const fmtUSDT = (v: number) => {
  const abs = Math.abs(v)
  const sign = v < 0 ? '-' : ''
  return `${sign}${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// 상단 알림 카드 색상 (2026-08-09 사장님) — 정적 클래스(Tailwind purge 안전).
// 알림 있음: 배경 진하게+글자 선명+테두리 색상 깜빡(animate-blink-border). 없음: 전부 회색.
const ALERT_BASE = 'p-2 rounded-lg cursor-pointer transition-all duration-200 flex-shrink-0 min-w-[100px] h-[42px]'
const ALERT_IDLE = 'border border-dark-700 bg-dark-800/40 text-dark-500'
const ALERT_ACTIVE: Record<string, string> = {
  blue:   'border-2 border-current bg-blue-500/25 text-blue-300 animate-blink-border shadow-lg shadow-blue-500/20',
  green:  'border-2 border-current bg-green-500/25 text-green-300 animate-blink-border shadow-lg shadow-green-500/20',
  yellow: 'border-2 border-current bg-yellow-500/25 text-yellow-300 animate-blink-border shadow-lg shadow-yellow-500/20',
  purple: 'border-2 border-current bg-purple-500/25 text-purple-300 animate-blink-border shadow-lg shadow-purple-500/20',
  red:    'border-2 border-current bg-red-500/25 text-red-300 animate-blink-border shadow-lg shadow-red-500/20',
  indigo: 'border-2 border-current bg-indigo-500/25 text-indigo-300 animate-blink-border shadow-lg shadow-indigo-500/20',
  orange: 'border-2 border-current bg-orange-500/25 text-orange-300 animate-blink-border shadow-lg shadow-orange-500/20',
}
const alertCls = (color: string, active: boolean) =>
  `${ALERT_BASE} ${active ? (ALERT_ACTIVE[color] || ALERT_ACTIVE.blue) : ALERT_IDLE}`

const Header = ({ onMenuClick }: HeaderProps) => {
  const { t } = useTranslation(['header', 'common'])
  const navigate = useNavigate()
  const { admin, logout } = useAuthStore()
  const {
    notifications,
    unreadCount,
    isConnected,
    connect,
    disconnect,
    markAsRead,
    markAllAsRead
  } = useNotificationStore()

  const {
    pendingRegistrations,
    pendingDeposits,
    pendingWithdrawals,
    pendingInquiries,
    pendingChats,
    pendingPartnerWithdrawals,
    unreadChatMessages,
    connectPendingCounts,
    disconnectPendingCounts
  } = usePendingCountsStore()

  const { count: unreadHandover, refresh: refreshHandover } = useHandoverUnreadStore()
  // 인수인계 미확인 폴링(45초) + 페이지에서 즉시 갱신용 전역 훅
  useEffect(() => {
    refreshHandover()
    ;(window as any).__refreshHandoverBadge = refreshHandover
    const iv = setInterval(refreshHandover, 45000)
    return () => { clearInterval(iv); try { delete (window as any).__refreshHandoverBadge } catch { /* noop */ } }
  }, [refreshHandover])

  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [headerStats, setHeaderStats] = useState<HeaderStats | null>(null)

  // Web Push 권한/구독 상태 (UI 표시용)
  const [pushState, setPushState] = useState<'unsupported' | 'denied' | 'default' | 'granted-no-sub' | 'granted-sub'>('default')
  const [pushBusy, setPushBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    const refresh = async () => {
      if (!isPushSupported()) {
        if (!cancelled) setPushState('unsupported')
        return
      }
      const perm = getPermissionState()
      if (perm === 'denied') {
        if (!cancelled) setPushState('denied')
        return
      }
      if (perm === 'default') {
        if (!cancelled) setPushState('default')
        return
      }
      // granted: 실제 구독 있는지
      const sub = await getCurrentSubscription()
      if (!cancelled) setPushState(sub ? 'granted-sub' : 'granted-no-sub')
    }
    refresh()
    return () => {
      cancelled = true
    }
  }, [])

  const handlePushToggle = useCallback(async () => {
    if (pushBusy) return
    setPushBusy(true)
    try {
      if (pushState === 'granted-sub') {
        await disablePush()
        setPushState('granted-no-sub')
      } else if (pushState === 'denied') {
        alert('브라우저 알림이 차단되어 있습니다. 주소창 좌측 자물쇠 아이콘 → 알림 → 허용으로 변경 후 다시 시도해주세요.')
      } else {
        const result = await enablePush()
        if (result.ok) {
          setPushState('granted-sub')
          // 곧바로 테스트 1발 (사용자에게 동작 확인)
          await sendTestPush()
        } else if (result.reason === 'denied') {
          setPushState('denied')
          alert('알림 권한이 거부되었습니다.')
        } else if (result.reason === 'unsupported') {
          alert('이 브라우저는 백그라운드 알림을 지원하지 않습니다.')
        } else {
          alert(`알림 활성화 실패: ${result.reason || 'unknown'}`)
        }
      }
    } finally {
      setPushBusy(false)
    }
  }, [pushState, pushBusy])

  const notificationRef = useRef<HTMLDivElement>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // 오늘 접속/거래 회원 명단 모달 (2026-07-26 사장님)
  const [activityModal, setActivityModal] = useState<'visitors' | 'traders' | null>(null)
  const [activityItems, setActivityItems] = useState<any[]>([])
  const [activityLoading, setActivityLoading] = useState(false)
  const [activityIncludeTest, setActivityIncludeTest] = useState(false)

  const loadActivityList = useCallback(async (kind: 'visitors' | 'traders', includeTest: boolean) => {
    try {
      setActivityLoading(true)
      const client = apiService.getClient()
      const res = await client.get(`/api/v1/admin/dashboard/today-${kind}?include_test=${includeTest}`)
      setActivityItems(res.data?.data?.items || [])
    } catch (e) {
      console.error(e)
      setActivityItems([])
    } finally {
      setActivityLoading(false)
    }
  }, [])

  // 명단 표시명 = 이름(닉네임) — 2026-07-29 사장님. 이름 없으면 닉네임만, 동일하면 한 번만
  const activityDisplayName = (it: any) => it.name
    ? (it.nickname && it.nickname !== it.name ? `${it.name}(${it.nickname})` : it.name)
    : (it.nickname || `#${it.user_id}`)

  const openActivityModal = (kind: 'visitors' | 'traders') => {
    setActivityModal(kind)
    setActivityIncludeTest(false)
    loadActivityList(kind, false)
  }

  const toggleActivityTest = () => {
    if (!activityModal) return
    const next = !activityIncludeTest
    setActivityIncludeTest(next)
    loadActivityList(activityModal, next)
  }

  const downloadActivityCsv = () => {
    if (!activityModal) return
    const isV = activityModal === 'visitors'
    const kstDate = new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10)
    const header = isV
      ? ['회원ID', '닉네임', '이름', '이메일', '테스트', '접속기록수', '직접로그인수', '오늘가입', '첫접속(KST)', '마지막접속(KST)']
      : ['회원ID', '닉네임', '이름', '이메일', '테스트', '코인체결', '신선물체결', '해외선물체결', '총체결', '첫거래(KST)', '마지막거래(KST)']
    const fmt = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const rows = activityItems.map((it: any) => isV
      ? [it.user_id, it.nickname, it.name, it.email, it.is_test_account ? 'Y' : '', it.log_count, it.login_count, it.is_new_signup ? 'Y' : '', (it.first_at || '').replace('T', ' ').slice(0, 19), (it.last_at || '').replace('T', ' ').slice(0, 19)]
      : [it.user_id, it.nickname, it.name, it.email, it.is_test_account ? 'Y' : '', it.coin_count, it.nf_count, it.ofut_count, it.total_count, (it.first_at || '').replace('T', ' ').slice(0, 19), (it.last_at || '').replace('T', ' ').slice(0, 19)])
    const csv = '\uFEFF' + [header, ...rows].map(r => r.map(fmt).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `${isV ? '오늘접속회원' : '오늘거래회원'}_${kstDate}${activityIncludeTest ? '_테스트포함' : ''}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const fetchHeaderStats = useCallback(async () => {
    try {
      const stats = await dashboardService.getHeaderStats()
      setHeaderStats(stats)
    } catch (e) { /* silent */ }
  }, [])

  useEffect(() => {
    if (admin?.id) {
      fetchHeaderStats()
      const interval = setInterval(fetchHeaderStats, 60000)
      return () => clearInterval(interval)
    }
  }, [admin?.id, fetchHeaderStats])

  // Connect to WebSockets when admin is available
  useEffect(() => {
    console.log('🔔 Header useEffect 실행:', { adminId: admin?.id, admin: admin, timestamp: new Date().toISOString() })

    if (admin?.id) {
      console.log('🔔 웹소켓 연결 시작:', admin.id)
      connect(admin.id)
      connectPendingCounts(admin.id)

      const { loadSoundSettings } = useNotificationStore.getState()
      loadSoundSettings()

      return () => {
        console.log('🔔 웹소켓 연결 해제:', admin.id)
        disconnect()
        disconnectPendingCounts()
      }
    } else {
      console.warn('🔔 웹소켓 연결 실패: admin.id가 없습니다', { admin })
      const retryTimeout = setTimeout(() => {
        const { admin: retryAdmin } = useAuthStore.getState()
        if (retryAdmin?.id) {
          console.log('🔔 재시도: 웹소켓 연결 시작:', retryAdmin.id)
          connect(retryAdmin.id)
          connectPendingCounts(retryAdmin.id)
        }
      }, 1000)
      return () => { clearTimeout(retryTimeout) }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin?.id])

  // 안읽은 채팅 반복 알람 — interval 분마다 unread > 0 이면 사운드 재생
  useEffect(() => {
    if (!admin?.id) return
    let timerId: number | null = null
    let lastFiredAt = 0

    const tick = () => {
      const { chatUnreadAlarm, playNotificationSound } = useNotificationStore.getState()
      const { unreadChatMessages } = usePendingCountsStore.getState()
      if (!chatUnreadAlarm.enabled) return
      if (!unreadChatMessages || unreadChatMessages <= 0) return

      const intervalMs = Math.max(1, chatUnreadAlarm.intervalMinutes) * 60 * 1000
      const now = Date.now()
      if (now - lastFiredAt < intervalMs) return

      lastFiredAt = now
      try {
        playNotificationSound(`안읽은 채팅 ${unreadChatMessages}건이 있습니다`, 'chat_unread')
      } catch (e) {
        console.warn('chat_unread 알람 재생 실패:', e)
      }
    }

    // 10초마다 조건 확인 (정확한 1분 정밀도 필요 없음 — 간격은 lastFiredAt 으로 게이트)
    timerId = window.setInterval(tick, 10000)
    // 초기 1회 즉시 (페이지 진입 시 unread 가 이미 있으면 바로 발화)
    tick()

    return () => {
      if (timerId !== null) window.clearInterval(timerId)
    }
  }, [admin?.id])

  const handleLogout = async () => {
    disconnect()
    disconnectPendingCounts()
    await logout()
  }

  const handleNotificationClick = (notificationId: number, isRead: boolean) => {
    if (!isRead) {
      markAsRead(notificationId)
    }
  }

  const handleMarkAllRead = () => {
    markAllAsRead()
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (notificationRef.current && !notificationRef.current.contains(target)) {
        setNotificationsOpen(false)
      }
      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setUserMenuOpen(false)
      }
    }
    if (notificationsOpen || userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => { document.removeEventListener('mousedown', handleClickOutside) }
    }
  }, [notificationsOpen, userMenuOpen])

  return (
    <header className="glass border-b border-white/20 px-4 sm:px-6 py-2 sticky top-0 z-[60] rounded-none bg-dark-900/95">
      <div className="flex items-center justify-between">
        {/* Left side */}
        <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
          <motion.button
            onClick={onMenuClick}
            className="glass-button p-2 rounded-lg lg:hidden"
            whileTap={{ scale: 0.95 }}
          >
            <Menu className="w-5 h-5" />
          </motion.button>
        </div>

        {/* Center - Pending Counts Cards (Hidden on mobile) */}
        <div className="hidden lg:flex flex-1 mx-4">
          <div className="flex items-center space-x-2 overflow-x-auto hide-scrollbar">
            {[
              { label: t('header:newSignups'), count: pendingRegistrations, color: 'blue', icon: UserPlus, path: '/admin/users' },
              { label: t('header:depositRequests'), count: pendingDeposits, color: 'green', icon: ArrowDownCircle, path: '/admin/deposits-withdrawals-management' },
              { label: t('header:withdrawalRequests'), count: pendingWithdrawals, color: 'yellow', icon: ArrowUpCircle, path: '/admin/deposits-withdrawals-management' },
              { label: t('header:inquiries'), count: pendingInquiries, color: 'purple', icon: HelpCircle, path: '/admin/inquiries' },
              { label: '인수인계', count: unreadHandover, color: 'indigo', icon: ClipboardList, path: '/admin/handover' },
            ].map(item => {
              const active = (item.count || 0) > 0
              return (
              <motion.div
                key={item.label}
                onClick={() => navigate(item.path)}
                className={alertCls(item.color, active)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center space-x-2">
                  <item.icon className="w-4 h-4 text-current shrink-0" />
                  <div>
                    <p className="text-[10px] leading-none opacity-70">{item.label}</p>
                    <p className="text-sm font-bold text-current leading-none mt-0.5">{item.count}</p>
                  </div>
                </div>
              </motion.div>
            )})}

            {/* 채팅 카드: 채팅 / 안읽음 두 칸으로 분할 — 둘 중 하나라도 있으면 카드 강조+테두리 깜빡 */}
            {(() => {
              const chatActive = (pendingChats || 0) > 0 || (unreadChatMessages || 0) > 0
              return (
              <motion.div
                onClick={() => navigate('/admin/chat')}
                className={`${ALERT_BASE.replace('min-w-[100px] ', '')} ${chatActive ? ALERT_ACTIVE.red : ALERT_IDLE}`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center space-x-2 h-full">
                  <MessageCircle className="w-4 h-4 text-current shrink-0" />
                  <div className="flex items-stretch">
                    <div className="text-center px-1.5">
                      <p className="text-[10px] leading-none opacity-70">{t('header:chats')}</p>
                      <p className={`text-sm font-bold leading-none mt-0.5 ${pendingChats > 0 ? 'text-current' : 'text-dark-500'}`}>{pendingChats}</p>
                    </div>
                    <div className="w-px bg-white/15 mx-0.5"></div>
                    <div className="text-center px-1.5 relative">
                      <p className="text-[10px] leading-none opacity-70">{t('header:unread', '안읽음')}</p>
                      <p className={`text-sm font-bold leading-none mt-0.5 ${unreadChatMessages > 0 ? 'text-current' : 'text-dark-500'}`}>
                        {unreadChatMessages}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )})()}

            {[
              { label: t('header:partnerWithdrawals'), count: pendingPartnerWithdrawals, color: 'orange', icon: ArrowUpCircle, path: '/admin/partner-withdrawals' },
            ].map(item => {
              const active = (item.count || 0) > 0
              return (
              <motion.div
                key={item.label}
                onClick={() => navigate(item.path)}
                className={alertCls(item.color, active)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center space-x-2">
                  <item.icon className="w-4 h-4 text-current shrink-0" />
                  <div>
                    <p className="text-[10px] leading-none opacity-70">{item.label}</p>
                    <p className="text-sm font-bold text-current leading-none mt-0.5">{item.count}</p>
                  </div>
                </div>
              </motion.div>
            )})}

            {/* 구분선 */}
            {headerStats && <div className="w-px h-8 bg-white/15 flex-shrink-0"></div>}

            {/* 당일 통계 카드 */}
            {headerStats && (
              <>
                {/* 입금 */}
                <motion.div
                  onClick={() => navigate('/admin/deposits-history')}
                  className="glass-button p-2 rounded-lg cursor-pointer transition-all duration-200 flex-shrink-0 min-w-[120px] h-[42px] hover:bg-green-500/10 hover:border-green-500/30"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-2">
                    <ArrowDownCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-dark-400 leading-none">{t('header:deposit')}</p>
                      <p className="text-xs font-bold text-green-400 leading-none mt-0.5">${fmtUSDT(headerStats.deposit_usdt)}</p>
                      <p className="text-xs font-bold text-green-400 leading-none mt-0.5">{t('Header.k_c404cb', { val0: fmtKRW(headerStats.deposit_krw) })}</p>
                    </div>
                  </div>
                </motion.div>

                {/* 출금 */}
                <motion.div
                  onClick={() => navigate('/admin/withdrawals-history')}
                  className="glass-button p-2 rounded-lg cursor-pointer transition-all duration-200 flex-shrink-0 min-w-[120px] h-[42px] hover:bg-red-500/10 hover:border-red-500/30"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-2">
                    <ArrowUpCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-dark-400 leading-none">{t('header:withdrawal')}</p>
                      <p className="text-xs font-bold text-red-400 leading-none mt-0.5">${fmtUSDT(headerStats.withdrawal_usdt)}</p>
                      <p className="text-xs font-bold text-red-400 leading-none mt-0.5">{t('Header.k_c404cb', { val0: fmtKRW(headerStats.withdrawal_krw) })}</p>
                    </div>
                  </div>
                </motion.div>

                {/* 순수익 */}
                <motion.div
                  onClick={() => navigate('/admin/daily-statistics')}
                  className={`glass-button p-2 rounded-lg cursor-pointer transition-all duration-200 flex-shrink-0 min-w-[120px] h-[42px] ${
                    headerStats.net_usdt >= 0
                      ? 'hover:bg-emerald-500/10 hover:border-emerald-500/30'
                      : 'hover:bg-orange-500/10 hover:border-orange-500/30'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-2">
                    <TrendingUp className={`w-4 h-4 flex-shrink-0 ${headerStats.net_usdt >= 0 ? 'text-emerald-400' : 'text-orange-400'}`} />
                    <div>
                      <p className="text-[10px] text-dark-400 leading-none">{t('header:netProfit')}</p>
                      <p className={`text-xs font-bold leading-none mt-0.5 ${headerStats.net_usdt >= 0 ? 'text-emerald-400' : 'text-orange-400'}`}>
                        {headerStats.net_usdt >= 0 ? '+' : ''}${fmtUSDT(headerStats.net_usdt)}
                      </p>
                      <p className={`text-xs font-bold leading-none mt-0.5 ${headerStats.net_krw >= 0 ? 'text-emerald-400' : 'text-orange-400'}`}>{headerStats.net_krw >= 0 ? '+' : ''}{fmtKRW(headerStats.net_krw)}{t('common:krwUnit')}</p>
                    </div>
                  </div>
                </motion.div>

                {/* 오늘 거래 회원 (테스트 제외, 2026-07-26 사장님) */}
                <motion.div
                  onClick={() => openActivityModal('traders')}
                  className="glass-button p-2 rounded-lg cursor-pointer transition-all duration-200 flex-shrink-0 min-w-[100px] h-[42px] hover:bg-amber-500/10 hover:border-amber-500/30"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-2">
                    <CandlestickChart className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-dark-400 leading-none">{t('header:todayTraders')}</p>
                      <p className="text-sm font-bold text-amber-400 leading-none mt-0.5">{headerStats.today_traders ?? 0}</p>
                    </div>
                  </div>
                </motion.div>

                {/* 오늘 접속 회원 (로그인 회원만·테스트 제외, 2026-07-26 사장님) */}
                <motion.div
                  onClick={() => openActivityModal('visitors')}
                  className="glass-button p-2 rounded-lg cursor-pointer transition-all duration-200 flex-shrink-0 min-w-[100px] h-[42px] hover:bg-sky-500/10 hover:border-sky-500/30"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-2">
                    <LogIn className="w-4 h-4 text-sky-400 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-dark-400 leading-none">{t('header:todayVisitors')}</p>
                      <p className="text-sm font-bold text-sky-400 leading-none mt-0.5">{headerStats.today_visitors ?? 0}</p>
                    </div>
                  </div>
                </motion.div>

                {/* 당일신규가입 */}
                <motion.div
                  onClick={() => navigate('/admin/users')}
                  className="glass-button p-2 rounded-lg cursor-pointer transition-all duration-200 flex-shrink-0 min-w-[100px] h-[42px] hover:bg-cyan-500/10 hover:border-cyan-500/30"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-2">
                    <UserPlus className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-dark-400 leading-none">{t('header:todaySignups')}</p>
                      <p className="text-sm font-bold text-cyan-400 leading-none mt-0.5">{headerStats.today_signups}</p>
                    </div>
                  </div>
                </motion.div>

                {/* 현재 포지션 (코인/국내 + 구해외 + 신선물) */}
                <motion.div
                  onClick={() => navigate('/admin/positions')}
                  className="glass-button p-2 rounded-lg cursor-pointer transition-all duration-200 flex-shrink-0 min-w-[100px] h-[42px] hover:bg-violet-500/10 hover:border-violet-500/30"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-2">
                    <Activity className="w-4 h-4 text-violet-400 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-dark-400 leading-none">{t('header:positions')}</p>
                      <p className="text-sm font-bold text-violet-400 leading-none mt-0.5">{headerStats.open_positions}</p>
                    </div>
                  </div>
                </motion.div>

                {/* 실시간 통계 — 회원별통계 '실시간 현황' 탭 대상 인원, 클릭 시 해당 탭 이동 */}
                <motion.div
                  onClick={() => navigate('/admin/user-statistics?tab=realtime')}
                  className="glass-button p-2 rounded-lg cursor-pointer transition-all duration-200 flex-shrink-0 min-w-[100px] h-[42px] hover:bg-rose-500/10 hover:border-rose-500/30"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-2">
                    <Radio className="w-4 h-4 text-rose-400 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-dark-400 leading-none">{t('header:realtimeStats')}</p>
                      <p className="text-sm font-bold text-rose-400 leading-none mt-0.5">{headerStats.realtime_members ?? 0}</p>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-2 flex-shrink-0">
          {/* Mobile: 실시간 채팅 안읽음 버튼 (lg에서는 헤더 가운데 카드로 보임) */}
          <motion.button
            onClick={() => navigate('/admin/chat')}
            className="relative glass-button p-2 rounded-lg lg:hidden"
            whileTap={{ scale: 0.95 }}
            aria-label={t('header:chats')}
          >
            <MessageCircle className={`w-5 h-5 ${unreadChatMessages > 0 ? 'text-red-400' : 'text-white'}`} />
            {unreadChatMessages > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold ring-2 ring-dark-900 animate-pulse">
                {unreadChatMessages > 99 ? '99+' : unreadChatMessages}
              </span>
            )}
          </motion.button>

          {/* Web Push 토글 (백그라운드 OS 알림) */}
          {pushState !== 'unsupported' && (
            <motion.button
              onClick={handlePushToggle}
              disabled={pushBusy}
              className="relative glass-button p-2 rounded-lg"
              whileTap={{ scale: 0.95 }}
              title={
                pushState === 'granted-sub'
                  ? '백그라운드 알림 활성화 — 클릭하여 끄기'
                  : pushState === 'denied'
                  ? '브라우저 차단됨'
                  : '백그라운드 알림 켜기'
              }
            >
              {pushState === 'granted-sub' ? (
                <BellRing className="w-5 h-5 text-emerald-400" />
              ) : pushState === 'denied' ? (
                <BellOff className="w-5 h-5 text-red-400" />
              ) : (
                <BellOff className="w-5 h-5 text-gray-400" />
              )}
            </motion.button>
          )}

          {/* Notification Bell */}
          <div className="relative" ref={notificationRef}>
            <motion.button
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              className="relative glass-button p-2 rounded-lg"
              whileTap={{ scale: 0.95 }}
            >
              <Bell className="w-5 h-5 text-white" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </motion.button>

            {/* Notification Dropdown */}
            <AnimatePresence>
              {notificationsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="fixed inset-x-4 sm:absolute sm:inset-x-auto top-16 sm:top-auto sm:right-0 sm:mt-2 w-auto sm:w-96 glass-dropdown rounded-2xl shadow-glass-lg z-[9999] max-h-[calc(100vh-5rem)] overflow-y-auto"
                >
                  <div className="p-3 sm:p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-dark-900/95 rounded-t-2xl z-10">
                    <h3 className="text-sm sm:text-base font-semibold text-white">{t('header:notifications')}</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs sm:text-sm text-primary-400 hover:text-primary-300 transition-colors"
                      >
                        {t('header:markAllRead')}
                      </button>
                    )}
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center">
                        <Bell className="w-12 h-12 text-dark-600 mx-auto mb-2" />
                        <p className="text-sm text-dark-400">{t('header:noNotifications')}</p>
                      </div>
                    ) : (
                      <div className="p-2">
                        {notifications.map((notification) => (
                          <motion.div
                            key={notification.id}
                            onClick={() => handleNotificationClick(notification.id, notification.is_read)}
                            className={`p-3 rounded-lg cursor-pointer transition-all mb-2 ${
                              notification.is_read
                                ? 'bg-dark-800/30 hover:bg-dark-800/50'
                                : 'bg-primary-500/10 hover:bg-primary-500/20 border border-primary-500/30'
                            }`}
                            whileTap={{ scale: 0.98 }}
                          >
                            <div className="flex items-start space-x-3">
                              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                                notification.is_read ? 'bg-dark-600' : 'bg-primary-500'
                              }`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white mb-1">{notification.title}</p>
                                <p className="text-xs text-dark-400 mb-2 line-clamp-2">{notification.content}</p>
                                <p className="text-xs text-dark-500">{formatNotificationTime(notification.created_at)}</p>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* User menu - Hidden on mobile */}
          <div className="relative hidden lg:block" ref={userMenuRef}>
            <motion.button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center space-x-3 glass-button px-3 py-2 rounded-lg"
              whileTap={{ scale: 0.95 }}
            >
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="text-left hidden md:block">
                <p className="text-sm font-medium text-white">{admin?.name}</p>
                <p className="text-xs text-dark-400">{admin?.username}</p>
              </div>
            </motion.button>

            <AnimatePresence>
              {userMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="fixed sm:absolute inset-x-4 sm:inset-x-auto top-16 sm:top-auto sm:right-0 sm:mt-2 w-auto sm:w-64 glass-dropdown rounded-2xl shadow-glass-lg z-[70]"
                >
                  <div className="p-3 sm:p-4 border-b border-white/10">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                        <User className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm sm:text-base font-semibold text-white">{admin?.name}</p>
                        <p className="text-xs sm:text-sm text-dark-400">{admin?.username}</p>
                        <div className="flex items-center space-x-1 mt-1">
                          <Shield className="w-3 h-3 text-primary-400" />
                          <span className="text-xs text-primary-400 font-medium">{t('header:admin')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-2">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-2 sm:space-x-3 px-2 sm:px-3 py-2 text-left hover:bg-red-500/10 rounded-lg transition-colors text-red-400"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="text-xs sm:text-sm">{t('header:logout')}</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      {/* 오늘 접속/거래 회원 명단 모달 (2026-07-26 사장님) */}
      {activityModal && createPortal(
        <div className="fixed inset-0 w-screen h-screen bg-black/70 backdrop-blur-sm flex items-center justify-center z-[99999] p-2 sm:p-4">
          <div className="glass-card w-full max-w-3xl max-h-[88vh] flex flex-col rounded-2xl border border-white/10">
            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-white/10">
              <div>
                <h2 className="text-lg font-bold text-white">
                  {activityModal === 'visitors' ? t('header:todayVisitorsModal') : t('header:todayTradersModal')}
                  <span className="ml-2 text-sm text-dark-400">({activityItems.length}{t('common:peopleUnit')})</span>
                </h2>
                <p className="text-[11px] text-dark-500 mt-0.5">{activityIncludeTest ? t('header:includingTest') : t('header:excludingTest')}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={toggleActivityTest}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${activityIncludeTest ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40' : 'bg-white/5 text-dark-300 border-white/10 hover:bg-white/10'}`}>
                  🧪 {t('header:showTest')}
                </button>
                <button onClick={downloadActivityCsv} disabled={activityItems.length === 0}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40 transition-colors">
                  {t('header:excelDownload')}
                </button>
                <button onClick={() => setActivityModal(null)} className="glass-button p-2 rounded-lg hover:bg-red-500/20">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto overflow-x-hidden p-3 sm:p-4">
              {activityLoading ? (
                <div className="py-14 text-center text-dark-400 text-sm">{t('common:loading')}</div>
              ) : activityItems.length === 0 ? (
                <div className="py-14 text-center text-dark-400 text-sm">{t('header:noActivity')}</div>
              ) : activityModal === 'visitors' ? (
                <table className="w-full text-sm table-fixed">
                  <thead>
                    <tr className="text-xs text-dark-400 border-b border-white/10">
                      <th className="text-left py-2 px-2 w-[42%]">{t('header:colMember')}</th>
                      <th className="text-right py-2 px-2">{t('header:colLogs')}</th>
                      <th className="text-right py-2 px-2">{t('header:colLogins')}</th>
                      <th className="text-right py-2 px-2">{t('header:colFirst')}</th>
                      <th className="text-right py-2 px-2">{t('header:colLast')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityItems.map((it: any) => (
                      <tr key={it.user_id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-2 px-2 cursor-pointer group"
                            title="회원 상세로 이동"
                            onClick={() => { setActivityModal(null); navigate(`/admin/users/${it.user_id}`) }}>
                          <p className="text-white group-hover:text-primary-300 group-hover:underline">
                            {activityDisplayName(it)}
                            {it.is_test_account && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">🧪</span>}
                            {it.is_new_signup && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">{t('header:newSignupTag')}</span>}
                          </p>
                          <p className="text-[11px] text-dark-400 truncate">#{it.user_id} · {it.email}</p>
                        </td>
                        <td className="py-2 px-2 text-right text-dark-200 tabular-nums">{it.log_count}</td>
                        <td className="py-2 px-2 text-right text-dark-200 tabular-nums">{it.login_count}</td>
                        <td className="py-2 px-2 text-right text-dark-300 text-xs whitespace-nowrap">{(it.first_at || '').slice(11, 16)}</td>
                        <td className="py-2 px-2 text-right text-dark-300 text-xs whitespace-nowrap">{(it.last_at || '').slice(11, 16)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-sm table-fixed">
                  <thead>
                    <tr className="text-xs text-dark-400 border-b border-white/10">
                      <th className="text-left py-2 px-2 w-[34%]">{t('header:colMember')}</th>
                      <th className="text-right py-2 px-2">{t('header:colCoin')}</th>
                      <th className="text-right py-2 px-2">{t('header:colNf')}</th>
                      <th className="text-right py-2 px-2">{t('header:colOfut')}</th>
                      <th className="text-right py-2 px-2">{t('header:colTotal')}</th>
                      <th className="text-right py-2 px-2">{t('header:colLast')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityItems.map((it: any) => (
                      <tr key={it.user_id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-2 px-2 cursor-pointer group"
                            title="회원 상세로 이동"
                            onClick={() => { setActivityModal(null); navigate(`/admin/users/${it.user_id}`) }}>
                          <p className="text-white group-hover:text-primary-300 group-hover:underline">
                            {activityDisplayName(it)}
                            {it.is_test_account && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">🧪</span>}
                          </p>
                          <p className="text-[11px] text-dark-400 truncate">#{it.user_id} · {it.email}</p>
                        </td>
                        <td className="py-2 px-2 text-right text-dark-200 tabular-nums">{it.coin_count || '-'}</td>
                        <td className="py-2 px-2 text-right text-dark-200 tabular-nums">{it.nf_count || '-'}</td>
                        <td className="py-2 px-2 text-right text-dark-200 tabular-nums">{it.ofut_count || '-'}</td>
                        <td className="py-2 px-2 text-right text-white font-bold tabular-nums">{it.total_count}</td>
                        <td className="py-2 px-2 text-right text-dark-300 text-xs whitespace-nowrap">{(it.last_at || '').slice(11, 16)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  )
}

const formatNotificationTime = (isoString: string): string => {
  try {
    let notificationTime: Date
    if (isoString.endsWith('Z')) {
      const utcTime = new Date(isoString)
      const koreaOffset = 9 * 60 * 60 * 1000
      notificationTime = new Date(utcTime.getTime() + koreaOffset)
    } else if (isoString.includes('+') || (isoString.includes('-') && isoString.length > 19)) {
      notificationTime = new Date(isoString)
    } else {
      const utcTime = new Date(isoString + 'Z')
      const koreaOffset = 9 * 60 * 60 * 1000
      notificationTime = new Date(utcTime.getTime() + koreaOffset)
    }
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - notificationTime.getTime()) / 1000)
    if (diffInSeconds < 0) {
      return i18n.t('common:time.justNow')
    } else if (diffInSeconds < 60) {
    return i18n.t('common:time.justNow')
  } else if (diffInSeconds < 3600) {
    return i18n.t('common:time.minutesAgo', { count: Math.floor(diffInSeconds / 60) })
  } else if (diffInSeconds < 86400) {
    return i18n.t('common:time.hoursAgo', { count: Math.floor(diffInSeconds / 3600) })
  } else if (diffInSeconds < 2592000) {
    return i18n.t('common:time.daysAgo', { count: Math.floor(diffInSeconds / 86400) })
  } else {
      const locale = i18n.language === 'ko' ? 'ko-KR' : i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'zh' ? 'zh-CN' : 'en-US'
      return notificationTime.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Seoul'
    })
    }
  } catch (e) {
    console.warn('Failed to format notification time:', e, 'isoString:', isoString)
    return i18n.t('common:time.unknown')
  }
}

export default Header
